import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Shield,
  Mail,
  User,
  Key,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const PERMISSION_LABELS = {
  stempeluhr: { label: 'Stempeluhr (Ein-/Ausstempeln)', description: 'Kann sich selbst ein- und ausstempeln' },
  dashboard: { label: 'Dashboard', description: 'Übersicht und Statistiken' },
  customers: { label: 'Kunden', description: 'Kundenverwaltung' },
  appointments: { label: 'Termine', description: 'Terminkalender' },
  files: { label: 'Dateien', description: 'Dateiverwaltung' },
  documents: { label: 'FarbWelt Office', description: 'Rechnungen, Angebote' },
  protocols: { label: 'Abnahmeprotokoll', description: 'Protokolle erstellen' },
  chats: { label: 'Kundenservice', description: 'Chat-Nachrichten' },
  gutachten: { label: 'Gutachten', description: 'KI-Gutachten erstellen' },
  aufmass: { label: 'Aufmaß', description: 'Aufmaß-Formulare' },
  einsatzplanung: { label: 'Einsatzplanung', description: 'Mitarbeiter & Einsätze planen' },
  zeiterfassung: { label: 'Zeiterfassung (Admin-Übersicht)', description: 'Alle Arbeitszeiten verwalten' },
  werkzeug: { label: 'Werkzeug & Lager', description: 'Werkzeug & Material verwalten' },
  payment_reminders: { label: 'Zahlungserinnerungen', description: 'Offene Zahlungen verwalten' },
  employee_payments: { label: 'Mitarbeiter-Zahlungen', description: 'Gehälter & Auszahlungen' },
  email_outbox: { label: 'Postausgang', description: 'Gesendete E-Mails' },
  auftrags_finder: { label: 'Auftrags-Finder', description: 'Neue Aufträge finden' },
  settings: { label: 'Einstellungen', description: 'Systemeinstellungen' },
};

const DEFAULT_PERMISSIONS = {
  stempeluhr: true, // Standard: neuer Mitarbeiter darf sich ein-/ausstempeln
  dashboard: false,
  customers: false,
  appointments: false,
  files: false,
  documents: false,
  protocols: false,
  chats: false,
  gutachten: false,
  aufmass: false,
  einsatzplanung: false,
  zeiterfassung: false,
  werkzeug: false,
  payment_reminders: false,
  employee_payments: false,
  email_outbox: false,
  auftrags_finder: false,
  settings: false,
  employees: false,
};

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Find-Email diagnostic tool
  const [showFindDialog, setShowFindDialog] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findResults, setFindResults] = useState([]);
  const [findLoading, setFindLoading] = useState(false);

  const runFind = async () => {
    if (!findQuery || findQuery.length < 2) { toast.error('Mindestens 2 Zeichen'); return; }
    setFindLoading(true);
    try {
      const res = await api.get('/admin/find-email', { params: { q: findQuery } });
      setFindResults(res.data?.matches || []);
      if ((res.data?.matches || []).length === 0) toast('Keine Treffer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suche fehlgeschlagen');
    } finally {
      setFindLoading(false);
    }
  };

  const deleteFoundEntry = async (r) => {
    if (!window.confirm(`Wirklich löschen?\n\n${r.name}\n${r.email || ''}\n(${r.role_label})`)) return;
    try {
      await api.delete('/admin/find-email/entry', { params: { collection: r.collection, id: r.id } });
      toast.success('Gelöscht');
      setFindResults(prev => prev.filter(x => !(x.collection === r.collection && x.id === r.id)));
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    permissions: { ...DEFAULT_PERMISSIONS }
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/admin/employees', { params: { _t: Date.now() } });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Fehler beim Laden der Mitarbeiter');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      permissions: { ...DEFAULT_PERMISSIONS }
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      email: employee.email,
      first_name: employee.first_name,
      last_name: employee.last_name,
      password: '',
      permissions: { ...DEFAULT_PERMISSIONS, ...employee.permissions }
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteDialog(true);
  };

  const handlePermissionChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: checked }
    }));
  };

  const createEmployee = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.password) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setIsSaving(true);
    try {
      await api.post('/admin/employees', {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        permissions: formData.permissions
      });
      toast.success('Mitarbeiter erfolgreich angelegt');
      setShowCreateDialog(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setIsSaving(false);
    }
  };

  const updateEmployee = async () => {
    if (!selectedEmployee) return;
    
    setIsSaving(true);
    try {
      await api.put(`/admin/employees/${selectedEmployee.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        permissions: formData.permissions
      });
      toast.success('Mitarbeiter aktualisiert');
      setShowEditDialog(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteEmployee = async () => {
    if (!selectedEmployee) return;
    
    setIsSaving(true);
    try {
      await api.delete(`/admin/employees/${selectedEmployee.id}`);
      toast.success('Mitarbeiter gelöscht');
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setIsSaving(false);
    }
  };

  const resetPassword = async (employee) => {
    setSelectedEmployee(employee);
    setIsSaving(true);
    try {
      const response = await api.post(`/admin/employees/${employee.id}/reset-password`);
      setNewPassword(response.data.new_password);
      setShowPasswordDialog(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setIsSaving(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(newPassword);
    toast.success('Passwort kopiert');
  };

  const toggleEmployeeStatus = async (employee) => {
    try {
      await api.put(`/admin/employees/${employee.id}`, {
        is_active: !employee.is_active
      });
      toast.success(employee.is_active ? 'Mitarbeiter deaktiviert' : 'Mitarbeiter aktiviert');
      fetchEmployees();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Fehler beim Ändern des Status');
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 md:w-7 md:h-7 text-[#9B1B30]" />
            Mitarbeiter-Verwaltung
          </h1>
          <p className="text-sm text-gray-600 mt-1">Mitarbeiter anlegen und Zugriffsrechte verwalten</p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-[#9B1B30] hover:bg-[#7a1526]"
          data-testid="create-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Mitarbeiter
        </Button>
        <Button
          variant="outline"
          onClick={() => { setFindQuery(''); setFindResults([]); setShowFindDialog(true); }}
          data-testid="find-email-btn"
        >
          <Search className="w-4 h-4 mr-2" />
          E-Mail finden
        </Button>
      </div>

      {/* Employee List */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-base md:text-lg">Alle Mitarbeiter ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Noch keine Mitarbeiter angelegt</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={openCreateDialog}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ersten Mitarbeiter anlegen
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {employees.map((employee) => (
                <div 
                  key={employee.id} 
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg gap-3 sm:gap-4 border ${employee.is_active !== false ? 'bg-gray-50 hover:bg-gray-100' : 'bg-red-50 border-red-200'}`}
                  data-testid={`employee-item-${employee.id}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${employee.is_active !== false ? 'bg-[#9B1B30]/10' : 'bg-red-100'}`}>
                      <User className={`w-5 h-5 ${employee.is_active !== false ? 'text-[#9B1B30]' : 'text-red-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm sm:text-base truncate">
                          {employee.first_name} {employee.last_name}
                        </p>
                        {employee.is_active === false && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Deaktiviert</span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {employee.email}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(employee.permissions || {}).filter(([_, v]) => v).map(([key]) => (
                          <span 
                            key={key} 
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                          >
                            {PERMISSION_LABELS[key]?.label || key}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 pl-13 sm:pl-0">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openEditDialog(employee)}
                      className="flex-1 sm:flex-none h-9"
                    >
                      <Pencil className="w-4 h-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden">Bearbeiten</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => resetPassword(employee)}
                      className="flex-1 sm:flex-none h-9"
                    >
                      <Key className="w-4 h-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden">Passwort</span>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleEmployeeStatus(employee)}
                      className={`flex-1 sm:flex-none h-9 ${employee.is_active !== false ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                    >
                      {employee.is_active !== false ? (
                        <><XCircle className="w-4 h-4 mr-1 sm:mr-0" /><span className="sm:hidden">Deaktiv.</span></>
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-1 sm:mr-0" /><span className="sm:hidden">Aktivieren</span></>
                      )}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openDeleteDialog(employee)}
                      className="flex-1 sm:flex-none h-9 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden">Löschen</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Find Email Diagnostic Dialog */}
      <Dialog open={showFindDialog} onOpenChange={setShowFindDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-[#9B1B30]" />
              E-Mail oder Namen finden
            </DialogTitle>
            <DialogDescription className="pt-2">
              Zeigt an, wo eine E-Mail-Adresse (oder ein Name) im System registriert ist — als Kunde, Mitarbeiter, Administrator oder Team-Mitglied.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Input
                value={findQuery}
                onChange={(e) => setFindQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runFind(); }}
                placeholder="z.B. ha8516747@gmail.com oder Hasan"
                data-testid="find-email-input"
              />
              <Button onClick={runFind} disabled={findLoading} className="bg-[#9B1B30] hover:bg-[#7a1526]" data-testid="find-email-search-btn">
                {findLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {findResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">{findResults.length} Treffer:</p>
                {findResults.map((r, i) => (
                  <div key={i} className="border rounded-lg p-3 bg-gray-50" data-testid={`find-result-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="min-w-0">
                        <p className="font-medium">{r.name}</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1"><Mail className="w-3 h-3" />{r.email || '-'}</p>
                      </div>
                      <div className="flex gap-2 items-center flex-wrap">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                          {r.role_label}
                        </span>
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                          {r.collection}
                        </span>
                        {r.is_active === false && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">deaktiviert</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 h-8"
                          onClick={() => deleteFoundEntry(r)}
                          data-testid={`find-delete-${i}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Löschen
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">ID: {r.id}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowFindDialog(false)}>Schließen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Employee Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#9B1B30]" />
              Neuen Mitarbeiter anlegen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vorname *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="Max"
                />
              </div>
              <div>
                <Label>Nachname *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Mustermann"
                />
              </div>
            </div>
            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="mitarbeiter@firma.de"
              />
            </div>
            <div>
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Sicheres Passwort"
              />
            </div>
            
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-[#9B1B30]" />
                Zugriffsrechte
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
                  <div 
                    key={key}
                    className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <Checkbox
                      id={`perm-${key}`}
                      checked={formData.permissions[key] || false}
                      onCheckedChange={(checked) => handlePermissionChange(key, checked)}
                    />
                    <div className="flex-1">
                      <label htmlFor={`perm-${key}`} className="text-sm font-medium cursor-pointer">
                        {label}
                      </label>
                      <p className="text-xs text-gray-500">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={createEmployee}
                disabled={isSaving}
                className="bg-[#9B1B30] hover:bg-[#7a1526]"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Anlegen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#9B1B30]" />
              Mitarbeiter bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vorname</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">E-Mail kann nicht geändert werden</p>
            </div>
            
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-[#9B1B30]" />
                Zugriffsrechte
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
                  <div 
                    key={key}
                    className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <Checkbox
                      id={`edit-perm-${key}`}
                      checked={formData.permissions[key] || false}
                      onCheckedChange={(checked) => handlePermissionChange(key, checked)}
                    />
                    <div className="flex-1">
                      <label htmlFor={`edit-perm-${key}`} className="text-sm font-medium cursor-pointer">
                        {label}
                      </label>
                      <p className="text-xs text-gray-500">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={updateEmployee}
                disabled={isSaving}
                className="bg-[#9B1B30] hover:bg-[#7a1526]"
              >
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Mitarbeiter löschen?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Möchten Sie <strong>{selectedEmployee?.first_name} {selectedEmployee?.last_name}</strong> wirklich löschen?
              <br /><br />
              <span className="text-red-600 font-medium">Diese Aktion kann nicht rückgängig gemacht werden.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteEmployee}
              disabled={isSaving}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Löschen...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Endgültig löschen</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Key className="w-5 h-5" />
              Neues Passwort generiert
            </DialogTitle>
            <DialogDescription className="pt-2">
              Das Passwort für <strong>{selectedEmployee?.first_name} {selectedEmployee?.last_name}</strong> wurde zurückgesetzt.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Label>Neues Passwort:</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={newPassword}
                readOnly
                className="font-mono bg-gray-100"
              />
              <Button variant="outline" onClick={copyPassword}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-orange-600 mt-2">
              ⚠️ Teilen Sie dieses Passwort sicher mit dem Mitarbeiter. Es wird nur einmal angezeigt!
            </p>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={() => {
              setShowPasswordDialog(false);
              setNewPassword('');
            }}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
